import type { Components, Theme } from '@mui/material/styles';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';

import { SHAPE, WEIGHT } from './scales';

const { notchedOutline, disabled, focused, error } = outlinedInputClasses;

/** Returns the corner, text colour and disabled look every field shares. */
function fieldOutline(theme: Theme) {
  return {
    borderRadius: SHAPE.extraSmall,
    color: theme.vars.palette.text.primary,
    '&.Mui-disabled': { backgroundColor: theme.vars.palette.action.hover, color: theme.vars.palette.text.secondary },
  };
}

/** The kit fields: outlined inputs and their looks, labels, helper text, the list trigger and the swatch. */
export const FIELDS: Components<Theme> = {
  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }) => ({ '&::placeholder': { color: theme.vars.palette.text.secondary, opacity: 1 } }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...fieldOutline(theme),
        [`& .${notchedOutline}`]: { borderColor: theme.vars.palette.outline },
        [`&:hover:not(.${disabled}, .${focused}, .${error}) .${notchedOutline}`]: {
          borderColor: theme.vars.palette.grey[500],
        },
        [`&.${focused}:not(.${error}) .${notchedOutline}`]: { borderColor: theme.vars.palette.text.brand },
      }),
    },
  },
  KitField: {
    variants: [
      {
        props: { highlighted: true },
        style: ({ theme }) => ({
          [`&:not(.${focused}, .${error}) .${notchedOutline}`]: { borderColor: theme.vars.palette.tint.brand.border },
        }),
      },
      { props: { textSize: 'sm' }, style: ({ theme }) => theme.typography.body2 },
      { props: { textSize: 'strong' }, style: { fontWeight: WEIGHT.bold } },
      { props: { textSize: 'lg' }, style: ({ theme }) => ({ ...theme.typography.specimenSmall }) },
      {
        props: { textSize: 'title' },
        style: ({ theme }) => ({ ...theme.typography.h4, color: theme.vars.palette.text.heading }),
      },
      {
        props: { mono: true },
        style: { '& input, & textarea': { fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' } },
      },
      { props: { uppercase: true }, style: { '& input': { textTransform: 'uppercase' } } },
      {
        props: { quiet: true },
        style: ({ theme }) => ({
          [`&:not(.${focused}) .${notchedOutline}`]: { borderColor: 'transparent' },
          [`&:hover:not(.${focused})`]: { backgroundColor: theme.vars.palette.action.hover },
        }),
      },
      {
        props: { prompting: true },
        style: ({ theme }) => ({
          [`&:not(.${focused})`]: {
            backgroundColor: theme.vars.palette.tint.brand.fill,
            [`& .${notchedOutline}`]: {
              borderWidth: 2,
              borderStyle: 'dashed',
              borderColor: theme.vars.palette.primary.light,
            },
            '& input::placeholder, & textarea::placeholder': { color: theme.vars.palette.tint.brand.text },
          },
        }),
      },
    ],
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.body1,
        color: theme.vars.palette.text.primary,
        '&.Mui-focused': { color: theme.vars.palette.text.primary },
      }),
      asterisk: ({ theme }) => ({ color: theme.vars.palette.error.main }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({ ...theme.typography.caption, marginInline: 0 }),
    },
  },
  KitFieldTrigger: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...fieldOutline(theme),
        ...theme.typography.body1,
        border: `1px solid ${theme.vars.palette.outline}`,
        '&:hover:not(.Mui-disabled)': { borderColor: theme.vars.palette.grey[500] },
        '& svg': { color: theme.vars.palette.text.secondary },
        '&.Mui-focusVisible': { borderColor: theme.vars.palette.text.brand },
      }),
    },
    variants: [{ props: { muted: true }, style: ({ theme }) => ({ color: theme.vars.palette.text.secondary }) }],
  },
  KitColorSwatch: {
    styleOverrides: {
      root: ({ theme }) => ({
        width: 28,
        height: 24,
        flexShrink: 0,
        padding: 0,
        cursor: 'pointer',
        borderRadius: SHAPE.compact,
        border: `1px solid ${theme.vars.palette.divider}`,
        '&::-webkit-color-swatch-wrapper': { padding: 0 },
        '&::-webkit-color-swatch': { border: 'none', borderRadius: SHAPE.extraSmall },
        '&::-moz-color-swatch': { border: 'none', borderRadius: SHAPE.extraSmall },
        '&:disabled': { cursor: 'not-allowed', opacity: 0.6 },
      }),
    },
  },
};
